import { useCallback, useRef } from 'react';
import { useSelector, useStore, type DefaultRootState } from 'react-redux';
import { appSlice } from '@bbkAdminRedux/app/app.slice';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import { useSignInRedirect } from '@bbkAdminRedux/app/useSignInRedirect';
import { resolveActiveContext } from '@bbkAdminRedux/app/utils';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import {
  appPersistSlice,
  selectLastActive,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectAuth } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { clearSessionMetadata } from '@bbkAdminRedux/rtkq/clear-session-metadata';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { hardRedirect } from '@bbkAdminUtils/browser-navigation';
import { type Maybe } from '@bbkAdminUtils/utility-types';

export const useAppInit = () => {
  const dispatch = useAppDispatch();
  const store = useStore();
  const { url: signInRedirectUri } = useSignInRedirect();
  // Local non-secret session metadata. Its presence lets us detect a stale
  // session when the `/api/current_user` probe returns no user (the credential
  // itself is an unreadable HttpOnly cookie).
  //
  // Read through a ref so the returned callback keeps a stable identity. Sign-in
  // writes this metadata, and AuthRoot's boot probe effect depends on the
  // callback: a new identity re-runs that probe mid-sign-in, and the resulting
  // `appInit(user)` can hard-navigate away while the company-creation POSTs are
  // still in flight.
  const staleSession = useSelector(selectAuth);
  const staleSessionRef = useRef(staleSession);
  staleSessionRef.current = staleSession;

  return useCallback(
    (user: Maybe<ActiveUser>) => {
      BrightbackSession.clearLegacyActiveContext();

      if (user) {
        const lastActive = selectLastActive(
          store.getState() as DefaultRootState
        );
        const { company, app, redirect } = resolveActiveContext(
          user,
          window.location.pathname,
          lastActive
        );

        dispatch(
          appSlice.actions.initializeActiveContext({
            activeCompanyKey: company?.internal_name,
            activeAppKey: app?.encoded_id,
          })
        );
        if (company) {
          dispatch(
            appPersistSlice.actions.saveLastActive({
              email: user.email,
              companyKey: company.internal_name,
              appKey: app?.encoded_id,
            })
          );
        }
        if (redirect) hardRedirect(redirect);
      } else if (staleSessionRef.current) {
        // The probe returned no user but stale local session metadata is still
        // present: the server session is gone, so drop local state and bounce
        // to sign-in with a security notice. No CBIDP server logout is issued
        // for an already-ended session.
        dispatch(appSlice.actions.clearActiveContext());
        BrightbackSession.setAuthAlert('Please log in again for security');
        clearSessionMetadata(dispatch);
        hardRedirect(signInRedirectUri);
      }
    },
    [dispatch, signInRedirectUri, store]
  );
};
